// src/pages/OrganisationPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { serverTimestamp } from 'firebase/firestore';
import { firestoreService } from '../services/firestoreService';
import { useAuth } from '../contexts/AuthContext';
import { 
  Building2, 
  Plus, 
  Save, 
  Loader2,
  CheckCircle2,
  AlertCircle,
  Camera,
  Upload,
  X,
  Edit,
  Trash2,
  Briefcase,
  Heart,
  Building,
  ChevronDown,
  MapPin,
  Phone,
  Mail,
  Globe as GlobeIcon,
  FileText,
  ToolCase
} from 'lucide-react';

const ORGANISATION_CATEGORIES = {
  business: {
    label: 'Business',
    icon: Briefcase,
    color: 'blue',
    subcategories: null
  },

  agency: {
    label: 'Agency',
    icon: ToolCase,
    color: 'Gray',
    subcategories: [
        {value: 'photography', label: 'Photographer'},
        {value: 'digital_cafe', label: 'Digital Cafe'},
        {value: 'web_developer', label: 'Web Developer'}
    ]
  },
  nonprofit: {
    label: 'Non-Profit',
    icon: Heart,
    color: 'green',
    subcategories: [
      { value: 'school', label: 'School' },
      { value: 'institution', label: 'Institution' },
      { value: 'church', label: 'Church' },
      { value: 'association', label: 'Association' },
      { value: 'labour_union', label: 'Labour Union' },
      { value: 'sports_club', label: 'Sports Club' },
      { value: 'cultural_group', label: 'Cultural Group' },
      { value: 'political_party', label: 'Political Party' },
      { value: 'forum', label: 'Forum' },
      { value: 'other', label: 'Other' }
    ]
  },
  government: {
    label: 'Government',
    icon: Building,
    color: 'purple',
    subcategories: [
      { value: 'department', label: 'Department' },
      { value: 'municipality', label: 'Municipality' },
      { value: 'agency', label: 'Agency' },
      { value: 'other', label: 'Other' }
    ]
  }
};

export const OrganisationPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [organisations, setOrganisations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    subcategory: '',
    registrationNumber: '',
    description: '',
    logoUrl: '',
    website: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      province: '',
      country: 'South Africa',
      postalCode: ''
    }
  });

  useEffect(() => {
    loadOrganisations();
  }, [user]);

  const loadOrganisations = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const userOrgs = await firestoreService.getUserOrganisations(user.uid);
      setOrganisations(userOrgs || []);
    } catch (err) {
      setError('Failed to load organisations');
      console.error('Error loading organisations:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      subcategory: '',
      registrationNumber: '',
      description: '',
      logoUrl: '',
      website: '',
      email: '',
      phone: '',
      address: {
        street: '',
        city: '',
        province: '',
        country: 'South Africa',
        postalCode: ''
      }
    });
    setEditingOrg(null);
    setShowForm(false);
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    setSuccess(false);
    setError(null);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingLogo(true);
      setError(null);

      const uploadResult = await firestoreService.uploadFile(
        file, 
        `pb-organisations/${user.uid}/logos`
      );

      setFormData(prev => ({ ...prev, logoUrl: uploadResult.url }));
    } catch (err) {
      setError(err.message || 'Failed to upload logo');
      console.error('Error uploading logo:', err);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      if (formData.logoUrl && formData.logoUrl.includes('firebase')) {
        await firestoreService.deleteFile(formData.logoUrl);
      }
      setFormData(prev => ({ ...prev, logoUrl: '' }));
    } catch (err) {
      console.error('Error removing logo:', err);
    }
  };

  const handleEdit = (org) => {
    setEditingOrg(org);
    setFormData({
      name: org.name,
      category: org.category,
      subcategory: org.subcategory || '',
      registrationNumber: org.registrationNumber || '',
      description: org.description || '',
      logoUrl: org.logoUrl || '',
      website: org.website || '',
      email: org.email || '',
      phone: org.phone || '',
      address: org.address || {
        street: '',
        city: '',
        province: '',
        country: 'South Africa',
        postalCode: ''
      }
    });
    setShowForm(true);
  };

  const handleDelete = async (orgId) => {
    if (!window.confirm('Are you sure you want to delete this organisation?')) return;

    try {
      setSaving(true);
      await firestoreService.deleteOrganisation(orgId, user.uid);
      setOrganisations(prev => prev.filter(org => org.id !== orgId));
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete organisation');
      console.error('Error deleting organisation:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      setError('Organisation name is required');
      return;
    }
    
    if (!formData.category) {
      setError('Please select a category');
      return;
    }

    if (ORGANISATION_CATEGORIES[formData.category]?.subcategories && !formData.subcategory) {
      setError('Please select a subcategory');
      return;
    }

    // Check limit (3 organisations max)
    if (!editingOrg && organisations.length >= 3) {
      setError('You can only register up to 3 organisations');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const orgData = {
        ...formData,
        ownerId: user.uid,
        ownerEmail: user.email,
        status: 'active',
        createdAt: editingOrg ? undefined : serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      let orgId;
      if (editingOrg) {
        // Update existing organisation
        await firestoreService.updateOrganisation(editingOrg.id, orgData);
        orgId = editingOrg.id;
        setOrganisations(prev => 
          prev.map(org => org.id === orgId ? { ...org, ...orgData } : org)
        );
      } else {
        // Create new organisation
        orgId = await firestoreService.createOrganisation(orgData, user.uid);
        const newOrg = { id: orgId, ...orgData };
        setOrganisations(prev => [...prev, newOrg]);
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        resetForm();
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to save organisation');
      console.error('Error saving organisation:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading organisations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                My Organisations
              </h1>
              <p className="text-slate-600">
                Manage your registered organisations ({organisations.length}/3)
              </p>
            </div>
            {!showForm && organisations.length < 3 && (
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-lg"
              >
                <Plus className="w-5 h-5" />
                Register Organisation
              </button>
            )}
          </div>
        </div>

        {/* Status Messages */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-green-800 font-medium">
              Organisation {editingOrg ? 'updated' : 'registered'} successfully!
            </p>
          </div>
        )}
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        )}

        {/* Registration Form */}
        {showForm && (
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900">
                {editingOrg ? 'Edit Organisation' : 'Register New Organisation'}
              </h2>
              <button
                onClick={resetForm}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Logo Upload */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">
                  Organisation Logo
                </label>
                <div className="flex items-center gap-4">
                  <div className="relative group">
                    {formData.logoUrl ? (
                      <>
                        <img
                          src={formData.logoUrl}
                          alt="Organisation Logo"
                          className="w-24 h-24 rounded-lg object-cover ring-2 ring-slate-200"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveLogo}
                          className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <div className="w-24 h-24 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Building2 className="w-10 h-10 text-slate-400" />
                      </div>
                    )}
                  </div>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium"
                  >
                    {uploadingLogo ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Upload Logo
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Organisation Name */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Organisation Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter organisation name"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                />
              </div>

              {/* Category Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => {
                      handleInputChange('category', e.target.value);
                      handleInputChange('subcategory', '');
                    }}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                  >
                    <option value="">Select category</option>
                    {Object.entries(ORGANISATION_CATEGORIES).map(([key, config]) => (
                      <option key={key} value={key}>
                        {config.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Subcategory Selection (conditional) */}
                {formData.category && ORGANISATION_CATEGORIES[formData.category]?.subcategories && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Type *
                    </label>
                    <select
                      value={formData.subcategory}
                      onChange={(e) => handleInputChange('subcategory', e.target.value)}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                    >
                      <option value="">Select type</option>
                      {ORGANISATION_CATEGORIES[formData.category].subcategories.map((sub) => (
                        <option key={sub.value} value={sub.value}>
                          {sub.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Registration Number */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Registration Number
                </label>
                <input
                  type="text"
                  value={formData.registrationNumber}
                  onChange={(e) => handleInputChange('registrationNumber', e.target.value)}
                  placeholder="Official registration number (optional)"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Brief description of your organisation"
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none resize-none"
                />
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="contact@organisation.com"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+27 XX XXX XXXX"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    <GlobeIcon className="w-4 h-4 inline mr-2" />
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://organisation.com"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  Address
                </label>
                <div className="grid grid-cols-1 gap-4">
                  <input
                    type="text"
                    value={formData.address.street}
                    onChange={(e) => handleInputChange('address.street', e.target.value)}
                    placeholder="Street address"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                      type="text"
                      value={formData.address.city}
                      onChange={(e) => handleInputChange('address.city', e.target.value)}
                      placeholder="City"
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                    />
                    <input
                      type="text"
                      value={formData.address.province}
                      onChange={(e) => handleInputChange('address.province', e.target.value)}
                      placeholder="Province"
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                    />
                    <input
                      type="text"
                      value={formData.address.postalCode}
                      onChange={(e) => handleInputChange('address.postalCode', e.target.value)}
                      placeholder="Postal Code"
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-6 border-t border-slate-200">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 sm:flex-initial px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-400 disabled:cursor-not-allowed transition-all font-semibold flex items-center justify-center gap-2 shadow-lg"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      {editingOrg ? 'Update' : 'Register'} Organisation
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Organisations List */}
        {organisations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {organisations.map((org) => {
              const categoryConfig = ORGANISATION_CATEGORIES[org.category];
              const CategoryIcon = categoryConfig?.icon || Building2;
              
              return (
                <div key={org.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  {/* Organisation Header */}
                  <div className={`h-32 bg-gradient-to-br from-${categoryConfig?.color || 'blue'}-500 to-${categoryConfig?.color || 'blue'}-600 relative`}>
                    {org.logoUrl ? (
                      <img
                        src={org.logoUrl}
                        alt={org.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <CategoryIcon className="w-16 h-16 text-white opacity-50" />
                      </div>
                    )}
                  </div>

                  {/* Organisation Content */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-slate-900 mb-1">
                          {org.name}
                        </h3>
                        <p className="text-sm text-slate-600">
                          {categoryConfig?.label}
                          {org.subcategory && ` • ${ORGANISATION_CATEGORIES[org.category].subcategories.find(s => s.value === org.subcategory)?.label}`}
                        </p>
                      </div>
                    </div>

                    {org.description && (
                      <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                        {org.description}
                      </p>
                    )}

                    {org.registrationNumber && (
                      <div className="mb-4 p-2 bg-slate-50 rounded text-xs text-slate-600">
                        <FileText className="w-3 h-3 inline mr-1" />
                        Reg: {org.registrationNumber}
                      </div>
                    )}

                    {/* Contact Info */}
                    <div className="space-y-2 mb-4 text-sm text-slate-600">
                      {org.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          <span className="truncate">{org.email}</span>
                        </div>
                      )}
                      {org.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          <span>{org.phone}</span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-4 border-t border-slate-200">
                      <button
                        onClick={() => handleEdit(org)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors font-medium"
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(org.id)}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors font-medium"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          !showForm && (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                No Organisations Registered
              </h3>
              <p className="text-slate-600 mb-6">
                Register your first organisation to get started
              </p>
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-lg"
              >
                <Plus className="w-5 h-5" />
                Register Organisation
              </button>
            </div>
          )
        )}
      </div>
    </div>
  );
};